/**
 * On-screen number pad for the tablet kiosk. Shop-floor counts run to the
 * hundreds, so a custom pad (not the OS keyboard, which covers half the screen)
 * with clean mono keys is the right entry surface — plus quick +10 / +50 chips
 * and a "Fill" that jumps to the size's remaining balance ("did the whole lot").
 * The value is a plain integer string owned by the parent; the readout itself
 * lives in the record sheet.
 */
import { Delete } from "lucide-react";
import { cn } from "@flint/ui";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function NumberPad({
  value,
  onChange,
  balance,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Remaining balance for the picked size — powers the "Fill" shortcut. */
  balance: number;
}) {
  const current = Number(value) || 0;

  const press = (digit: string) => {
    // Cap at 6 digits (max 1,000,000 per the wip contract) and drop a leading 0.
    const next = (value === "0" ? "" : value) + digit;
    if (next.length > 6) return;
    onChange(next);
  };
  const backspace = () => onChange(value.slice(0, -1));
  const addN = (n: number) => onChange(String(Math.max(0, current + n)));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Chip onClick={() => addN(10)}>+10</Chip>
        <Chip onClick={() => addN(50)}>+50</Chip>
        <Chip onClick={() => addN(-10)} disabled={current < 10}>
          −10
        </Chip>
        {balance > 0 ? (
          <Chip onClick={() => onChange(String(balance))} tone="fill">
            Fill {balance.toLocaleString("en-IN")}
          </Chip>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {KEYS.map((k) => (
          <PadKey key={k} onClick={() => press(k)}>
            {k}
          </PadKey>
        ))}
        <PadKey onClick={() => onChange("")} muted>
          Clear
        </PadKey>
        <PadKey onClick={() => press("0")}>0</PadKey>
        <PadKey onClick={backspace} muted aria-label="Backspace">
          <Delete className="h-6 w-6" />
        </PadKey>
      </div>
    </div>
  );
}

function PadKey({
  children,
  onClick,
  muted,
  "aria-label": ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  muted?: boolean;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        "grid h-16 place-items-center rounded-xl border border-line bg-paper font-mono text-2xl font-medium tabular-nums transition active:scale-[0.97] active:bg-raised",
        muted ? "font-sans text-base font-medium text-muted" : "text-ink hover:border-line-2",
      )}
    >
      {children}
    </button>
  );
}

function Chip({
  children,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "fill";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "h-10 rounded-full border px-4 text-sm font-semibold tabular-nums transition active:scale-[0.97] disabled:opacity-40",
        tone === "fill"
          ? "border-sky-600 bg-sky-50 text-sky-700"
          : "border-line bg-paper text-ink-2 hover:border-line-2",
      )}
    >
      {children}
    </button>
  );
}
